import type { NavigateFunction } from 'react-router-dom'

import {
  CalendarIcon,
  LightbulbIcon,
  MessagesSquareIcon,
  ChartColumnIcon,
} from '@buffer-mono/popcorn'

import { PRIORITIES } from '../constants'
import { createAction } from './createAction'
import type { CommandBarAction } from './types'

/**
 * Options for building navigation actions.
 */
interface BuildNavigationActionsOptions {
  /** React Router navigate function for navigation */
  navigate: NavigateFunction
  /** URL for the all channels page */
  allChannelsUrl: string
  /** URL for the comments/community page */
  commentsUrl: string
  /** Whether the user is on a legacy plan */
  isLegacyPlan: boolean
}

export function buildNavigationActions({
  navigate,
  allChannelsUrl,
  commentsUrl,
  isLegacyPlan,
}: BuildNavigationActionsOptions): CommandBarAction[] {
  const actions: CommandBarAction[] = []

  // Create/Ideas - not available on legacy plans
  if (!isLegacyPlan) {
    actions.push(
      createAction({
        id: 'nav-create-ideas',
        name: 'Ideas',
        prefix: 'Create',
        keywords: ['create', 'ideas', 'content', 'inspiration', 'brainstorm'],
        icon: <LightbulbIcon />,
        section: 'navigation',
        priority: PRIORITIES.ideas,
        perform: () => navigate('/create/ideas'),
      }),
    )
  }

  // Publish / All Channels
  actions.push(
    createAction({
      id: 'nav-publish',
      name: 'Publish',
      keywords: ['publish', 'queue', 'all channels', 'posts', 'schedule'],
      icon: <CalendarIcon />,
      section: 'navigation',
      priority: PRIORITIES.createPost,
      perform: () => navigate(allChannelsUrl),
    }),
  )

  // Calendar
  actions.push(
    createAction({
      id: 'nav-calendar',
      name: 'Calendar',
      keywords: ['calendar', 'schedule', 'week', 'month', 'planning'],
      icon: <CalendarIcon />,
      section: 'navigation',
      priority: PRIORITIES.calendar,
      perform: () => navigate('/all-channels/calendar/week'),
    }),
  )

  // Community - not available on legacy plans
  if (!isLegacyPlan) {
    actions.push(
      createAction({
        id: 'nav-community',
        name: 'Community',
        keywords: ['community', 'comments', 'engage', 'replies', 'messages'],
        icon: <MessagesSquareIcon />,
        section: 'navigation',
        priority: PRIORITIES.community,
        perform: () => navigate(commentsUrl),
      }),
    )
  }

  // Insights
  actions.push(
    createAction({
      id: 'nav-insights',
      name: 'Insights',
      keywords: ['insights', 'analytics', 'reports', 'metrics', 'performance'],
      icon: <ChartColumnIcon />,
      section: 'navigation',
      priority: PRIORITIES.analytics,
      perform: () => navigate('/insights'),
    }),
  )

  return actions
}
